/**
 * 商业类产品展示效果
 * 
 * 基于足球视频质量标准（9.0/10）制作
 * 
 * 核心功能：
 * 1. 产品名称 + 标语（动态文字）
 * 2. 产品特性展示（3 个核心卖点）
 * 3. 价格信息（数字滚动）
 * 4. CTA 按钮（号召行动）
 * 
 * 技术亮点：
 * - Spring 物理动画
 * - interpolate 插值计算
 * - 数字滚动效果
 * - 数据驱动可视化
 * - 模块化设计
 * - 详细注释
 * 
 * 适用场景：
 * - 产品发布会视频
 * - 电商产品展示
 * - 营销宣传视频
 * - 新品介绍视频
 * 
 * @author 龙虾菌 🦞
 * @date 2026-03-10
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

/**
 * 产品数据结构
 */
interface Product {
  name: string;
  tagline: string;
  price: number;
  currency: string;
  features: Feature[];
  cta: string;
}

/**
 * 产品特性数据结构
 */
interface Feature {
  icon: string;
  title: string;
  description: string;
}

// ==================== 配置数据 ====================

/**
 * 产品配置（可自定义修改）
 */
const PRODUCT_DATA: Product = {
  name: 'iPhone 16 Pro',
  tagline: '超强芯片，超凡性能',
  price: 7999,
  currency: '¥',
  features: [
    {
      icon: '⚡',
      title: 'A18 Pro 芯片',
      description: '业界领先的性能，游戏、剪辑快如闪电',
    },
    {
      icon: '📸',
      title: '4800 万像素',
      description: '专业级摄影系统，捕捉每个精彩瞬间',
    },
    {
      icon: '🔋',
      title: '全天候续航',
      description: '续航提升 30%，从早到晚不断电',
    },
  ],
  cta: '立即预订 →',
};

// ==================== 子组件 ====================

/**
 * 产品名称组件
 */
const ProductName: React.FC<{
  name: string;
  tagline: string;
  frame: number;
  fps: number;
}> = ({ name, tagline, frame, fps }) => {
  // 名称 Spring 动画
  const nameSpring = spring({
    frame,
    fps,
    config: {
      damping: 12,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // 标语延迟入场
  const taglineSpring = spring({
    frame: frame - 15,
    fps,
    config: {
      damping: 20,
      stiffness: 80,
    },
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: 100,
        left: 80,
      }}
    >
      {/* 产品名称 */}
      <h1
        style={{
          fontSize: 80,
          fontWeight: 'bold',
          background: 'linear-gradient(90deg, #fff 0%, #a5b4fc 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          margin: 0,
          textShadow: '0 0 60px rgba(165, 180, 252, 0.4)',
          transform: `translateY(${interpolate(nameSpring, [0, 1], [60, 0])}px)`,
          opacity: nameSpring,
        }}
      >
        {name}
      </h1>

      {/* 标语 */}
      <p
        style={{
          fontSize: 36,
          color: 'rgba(255, 255, 255, 0.9)',
          margin: '20px 0 0 0',
          transform: `translateY(${interpolate(taglineSpring, [0, 1], [40, 0])}px)`,
          opacity: taglineSpring,
          textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
        }}
      >
        {tagline}
      </p>
    </div>
  );
};

/**
 * 产品特性组件
 */
const FeatureCard: React.FC<{
  feature: Feature;
  index: number;
  frame: number;
  fps: number;
}> = ({ feature, index, frame, fps }) => {
  const delay = 40 + index * 20;

  // 卡片 Spring 动画
  const cardSpring = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 15,
      stiffness: 80,
      mass: 0.6,
    },
  });

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '30px',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        transform: `translateY(${interpolate(cardSpring, [0, 1], [50, 0])}px)`,
        opacity: cardSpring,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* 图标 */}
      <div
        style={{
          fontSize: 56,
          marginBottom: 20,
        }}
      >
        {feature.icon}
      </div>

      {/* 标题 */}
      <div
        style={{
          fontSize: 30,
          fontWeight: 'bold',
          color: '#fff',
          marginBottom: 10,
        }}
      >
        {feature.title}
      </div>

      {/* 描述 */}
      <div
        style={{
          fontSize: 26,
          color: 'rgba(255, 255, 255, 0.8)',
          lineHeight: 1.5,
        }}
      >
        {feature.description}
      </div>
    </div>
  );
};

/**
 * 价格组件（数字滚动）
 */
const PriceDisplay: React.FC<{
  price: number;
  currency: string;
  frame: number;
  fps: number;
}> = ({ price, currency, frame, fps }) => {
  const delay = 100;

  // 价格 Spring 动画
  const priceSpring = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 20,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // 数字滚动效果
  const displayPrice = Math.round(price * interpolate(frame, [delay, delay + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  }));

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 160,
        left: 80,
        transform: `translateY(${interpolate(priceSpring, [0, 1], [30, 0])}px)`,
        opacity: priceSpring,
      }}
    >
      <div
        style={{
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: 10,
        }}
      >
        起售价
      </div>
      <div
        style={{
          fontSize: 72,
          fontWeight: 'bold',
          color: '#ffd700',
          textShadow: '0 0 30px rgba(255, 215, 0, 0.6)',
        }}
      >
        {currency}
        {displayPrice.toLocaleString()}
      </div>
    </div>
  );
};

/**
 * CTA 按钮组件
 */
const CTAButton: React.FC<{
  text: string;
  frame: number;
  fps: number;
}> = ({ text, frame, fps }) => {
  const delay = 120;

  // 按钮 Spring 动画
  const buttonSpring = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 12,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // 悬停效果模拟（脉冲动画）
  const pulseProgress = interpolate(
    frame,
    [120, 135, 150],
    [1, 1.05, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 80,
        right: 80,
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        padding: '25px 50px',
        borderRadius: 50,
        transform: `scale(${pulseProgress * buttonSpring})`,
        opacity: buttonSpring,
        boxShadow: '0 10px 40px rgba(59, 130, 246, 0.6)',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          fontSize: 32,
          fontWeight: 'bold',
          color: '#fff',
        }}
      >
        {text}
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 商业类产品展示效果
 * 
 * 参数：
 * - product: Product - 产品数据（可选，默认使用 PRODUCT_DATA）
 */
export const ProductShowcase: React.FC<{ product?: Product }> = ({
  product = PRODUCT_DATA,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 背景装饰（网格） */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          opacity: 0.5,
        }}
      />

      {/* 产品名称 + 标语 */}
      <ProductName
        name={product.name}
        tagline={product.tagline}
        frame={frame}
        fps={fps}
      />

      {/* 产品特性 */}
      <div
        style={{
          position: 'absolute',
          top: 320,
          left: 80,
          right: 80,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 30,
        }}
      >
        {product.features.map((feature, index) => (
          <FeatureCard
            key={index}
            feature={feature}
            index={index}
            frame={frame}
            fps={fps}
          />
        ))}
      </div>

      {/* 价格 */}
      <PriceDisplay
        price={product.price}
        currency={product.currency}
        frame={frame}
        fps={fps}
      />

      {/* CTA 按钮 */}
      <CTAButton text={product.cta} frame={frame} fps={fps} />

      {/* 装饰元素（光晕） */}
      <div
        style={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      />
    </AbsoluteFill>
  );
};

// ==================== 默认配置 ====================

/**
 * 默认导出配置
 */
export default {
  id: 'ProductShowcase',
  component: ProductShowcase,
  durationInFrames: 150, // 5 秒 @ 30 FPS
  fps: 30,
  width: 1920,
  height: 1080,
};
